import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AdminPage from '../../components/admin/AdminPage';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import SectionCard from '../../components/admin/SectionCard';
import { EmptyState, LoadingState } from '../../components/admin/StateViews';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';
import { AuditLogService } from '../../services/AuditLogService';
import { AdminUserService } from '../../services/AdminUserService';
import { CircleService } from '../../services/CircleService';
import { AdminCircle, AdminCircleFilters, AdminUser } from '../../types';

const PAGE_SIZE = 8;

const initialFilters: AdminCircleFilters = {
  search: '',
  creator: '',
  memberCountMin: '',
  memberCountMax: '',
  createdFrom: '',
  createdTo: '',
  includeDeleted: false,
};

const initialCreateValues = {
  creatorUserId: '',
  name: '',
  description: '',
  coverPhotoUrl: '',
  coverPhotoStorageKey: '',
  isPublic: true,
  isFeatured: false,
};

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : 'Not set';

const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(value || 0);

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });

const CirclesListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const canEdit = ['SuperAdmin', 'Content-Manager'].includes(user?.role || '');
  const canDelete = user?.role === 'SuperAdmin';
  const [circles, setCircles] = useState<AdminCircle[]>([]);
  const [filters, setFilters] = useState<AdminCircleFilters>(initialFilters);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pendingDelete, setPendingDelete] = useState<AdminCircle | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createValues, setCreateValues] = useState(initialCreateValues);
  const [creating, setCreating] = useState(false);
  const [uploadingCoverPhoto, setUploadingCoverPhoto] = useState(false);
  const [updatingFeaturedId, setUpdatingFeaturedId] = useState<string | null>(null);
  const coverPhotoInputRef = useRef<HTMLInputElement | null>(null);

  const loadCircles = useCallback(async (nextFilters: AdminCircleFilters) => {
    try {
      setLoading(true);
      setError(null);
      setCircles(await CircleService.getCircles(nextFilters));
    } catch (loadError) {
      console.error(loadError);
      setError('Failed to load circles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCircles(initialFilters);
    AdminUserService.getUsers()
      .then(setUsers)
      .catch((loadUsersError) => console.error(loadUsersError));
  }, [loadCircles]);

  useEffect(() => {
    setPage(1);
  }, [circles]);

  const pageCount = Math.max(1, Math.ceil(circles.length / PAGE_SIZE));
  const paginatedCircles = useMemo(
    () => circles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [circles, page]
  );

  const handleDelete = async () => {
    if (!pendingDelete) {
      return;
    }

    try {
      await CircleService.deleteCircle(pendingDelete.id);
      setCircles((current) => current.filter((item) => item.id !== pendingDelete.id));
      await AuditLogService.recordEntry({
        module: 'circles',
        action: 'deleted',
        description: `${pendingDelete.name} was deleted from the admin circles console.`,
        actorEmail: user?.email || 'admin@local',
        targetId: pendingDelete.id,
        targetLabel: pendingDelete.name,
      });
      showToast('Circle deleted.');
    } catch (deleteError) {
      console.error(deleteError);
      setError('Failed to delete the selected circle.');
    } finally {
      setPendingDelete(null);
    }
  };

  const handleCreateCircle = async () => {
    if (!createValues.creatorUserId || !createValues.name.trim() || !createValues.description.trim()) {
      setError('Owner, circle name, and description are required.');
      return;
    }

    try {
      setCreating(true);
      setError(null);
      const created = await CircleService.createCircle({
        creatorUserId: createValues.creatorUserId,
        name: createValues.name.trim(),
        description: createValues.description.trim(),
        coverPhotoUrl: createValues.coverPhotoUrl.trim(),
        coverPhotoStorageKey: createValues.coverPhotoStorageKey.trim(),
        isPublic: createValues.isPublic,
        isFeatured: createValues.isFeatured,
      });
      setCreateOpen(false);
      setCreateValues(initialCreateValues);
      await loadCircles(filters);
      await AuditLogService.recordEntry({
        module: 'circles',
        action: 'created',
        description: `${created.name} was created on behalf of ${created.creator.displayName}.`,
        actorEmail: user?.email || 'admin@local',
        targetId: created.id,
        targetLabel: created.name,
      });
      showToast('Circle created.');
      navigate(`/circles/${created.id}`);
    } catch (createError) {
      console.error(createError);
      setError('Failed to create circle.');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateCoverPhotoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    try {
      setUploadingCoverPhoto(true);
      setError(null);
      const dataUrl = await readFileAsDataUrl(file);
      const uploaded = await CircleService.uploadCoverPhoto({
        dataUrl,
        fileName: file.name,
      });
      setCreateValues((current) => ({
        ...current,
        coverPhotoUrl: uploaded.url,
        coverPhotoStorageKey: uploaded.storageKey,
      }));
      showToast('Cover photo uploaded.');
    } catch (uploadError: any) {
      console.error(uploadError);
      const serverMessage =
        uploadError?.response?.data?.error?.message ||
        uploadError?.response?.data?.message ||
        uploadError?.message;
      setError(serverMessage || 'Failed to upload the cover photo.');
    } finally {
      setUploadingCoverPhoto(false);
      event.target.value = '';
    }
  };

  const handleToggleFeatured = async (circle: AdminCircle) => {
    try {
      setUpdatingFeaturedId(circle.id);
      setError(null);
      const updated = await CircleService.updateCircle(circle.id, {
        isFeatured: !circle.isFeatured,
      });
      setCircles((current) =>
        current.map((item) =>
          item.id === circle.id
            ? {
                ...item,
                isFeatured: updated.isFeatured,
                updatedAt: updated.updatedAt,
              }
            : item
        )
      );
      await AuditLogService.recordEntry({
        module: 'circles',
        action: updated.isFeatured ? 'featured' : 'unfeatured',
        description: `${updated.name} was ${
          updated.isFeatured ? 'marked as featured' : 'removed from featured'
        } in the admin circles console.`,
        actorEmail: user?.email || 'admin@local',
        targetId: updated.id,
        targetLabel: updated.name,
      });
      showToast(updated.isFeatured ? 'Circle marked as featured.' : 'Circle removed from featured.');
    } catch (featuredError) {
      console.error(featuredError);
      setError('Failed to update featured status.');
    } finally {
      setUpdatingFeaturedId(null);
    }
  };

  if (loading) {
    return <LoadingState label="Loading circles…" />;
  }

  return (
    <AdminPage
      title="Circles"
      description="Search, inspect, and safely retire community circles without touching donation or user records."
    >
      {error ? <Alert severity="error">{error}</Alert> : null}

      <SectionCard
        title="Circle directory"
        description="Filter by circle name, creator, member count, and creation date."
        action={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => {
              setFilters(initialFilters);
              loadCircles(initialFilters);
            }}>
              Reset
            </Button>
            <Button variant="contained" onClick={() => loadCircles(filters)}>
              Apply filters
            </Button>
            <Button variant="contained" onClick={() => setCreateOpen(true)}>
              Create circle
            </Button>
          </Stack>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search by name or description"
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Creator name or email"
              value={filters.creator}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  creator: event.target.value,
                }))
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Min members"
              type="number"
              value={filters.memberCountMin}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  memberCountMin: event.target.value
                    ? Number(event.target.value)
                    : '',
                }))
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Max members"
              type="number"
              value={filters.memberCountMax}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  memberCountMax: event.target.value
                    ? Number(event.target.value)
                    : '',
                }))
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Created from"
              type="date"
              value={filters.createdFrom}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  createdFrom: event.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Created to"
              type="date"
              value={filters.createdTo}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  createdTo: event.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(filters.includeDeleted)}
                    onChange={(event) =>
                      setFilters((current) => ({
                        ...current,
                        includeDeleted: event.target.checked,
                      }))
                    }
                  />
                }
                label="Include deleted circles"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(filters.isFeatured)}
                    onChange={(event) =>
                      setFilters((current) => ({
                        ...current,
                        isFeatured: event.target.checked ? true : undefined,
                      }))
                    }
                  />
                }
                label="Featured only"
              />
            </Stack>
          </Grid>
        </Grid>

        {circles.length === 0 ? (
          <EmptyState
            title="No circles match the current filters"
            description="Try a broader search or clear the date and member filters."
          />
        ) : (
          <>
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Circle</TableCell>
                    <TableCell>Creator</TableCell>
                    <TableCell align="right">Members</TableCell>
                    <TableCell align="right">Points</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCircles.map((circle) => (
                    <TableRow key={circle.id} hover>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography fontWeight={600}>{circle.name}</Typography>
                            {circle.isFeatured ? (
                              <Typography variant="caption" color="primary.main">
                                Featured
                              </Typography>
                            ) : null}
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {circle.description || 'No description'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {circle.isPublic ? 'Visible in discovery' : 'Hidden from discovery'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={600}>
                            {circle.creator.displayName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {circle.creatorEmail || 'No email available'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{formatNumber(circle.memberCount)}</TableCell>
                      <TableCell align="right">{formatNumber(circle.totalPoints)}</TableCell>
                      <TableCell>{formatDate(circle.createdAt)}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={circle.deletedAt ? 'error.main' : 'text.secondary'}
                        >
                          {circle.deletedAt ? 'Deleted' : 'Active'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(`/circles/${circle.id}`)}
                          >
                            View
                          </Button>
                          {canEdit && !circle.deletedAt ? (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleToggleFeatured(circle)}
                              disabled={updatingFeaturedId === circle.id}
                            >
                              {circle.isFeatured ? 'Unfeature' : 'Feature'}
                            </Button>
                          ) : null}
                          {canDelete && !circle.deletedAt ? (
                            <Button
                              variant="text"
                              size="small"
                              color="error"
                              onClick={() => setPendingDelete(circle)}
                            >
                              Delete
                            </Button>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Stack direction="row" justifyContent="flex-end">
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, nextPage) => setPage(nextPage)}
                color="primary"
              />
            </Stack>
          </>
        )}
      </SectionCard>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete circle?"
        description="This will soft delete the circle, clear default-circle links, revoke invites, remove memberships, and delete circle point transactions only."
        confirmLabel="Delete circle"
        confirmColor="error"
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDelete}
      />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create circle on behalf of a user</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              fullWidth
              label="Circle owner"
              value={createValues.creatorUserId}
              onChange={(event) =>
                setCreateValues((current) => ({
                  ...current,
                  creatorUserId: event.target.value,
                }))
              }
              SelectProps={{ native: true }}
            >
              <option value="">Select user</option>
              {users.map((owner) => (
                <option key={owner.id} value={owner.id}>
                  {owner.name} - {owner.email}
                </option>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Circle name"
              value={createValues.name}
              onChange={(event) =>
                setCreateValues((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Description"
              value={createValues.description}
              onChange={(event) =>
                setCreateValues((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
            />
            <TextField
              fullWidth
              label="Cover photo URL"
              value={createValues.coverPhotoUrl}
              onChange={(event) =>
                setCreateValues((current) => ({
                  ...current,
                  coverPhotoUrl: event.target.value,
                  coverPhotoStorageKey: '',
                }))
              }
            />
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="outlined"
                onClick={() => coverPhotoInputRef.current?.click()}
                disabled={uploadingCoverPhoto}
              >
                {uploadingCoverPhoto ? 'Uploading…' : 'Upload cover photo'}
              </Button>
              <input
                ref={coverPhotoInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleCreateCoverPhotoUpload}
              />
              {createValues.coverPhotoUrl ? (
                <Typography variant="body2" color="success.main">
                  Image ready
                </Typography>
              ) : null}
            </Stack>
            {createValues.coverPhotoUrl ? (
              <Box
                component="img"
                src={createValues.coverPhotoUrl}
                alt="Circle cover preview"
                sx={{
                  width: '100%',
                  maxHeight: 220,
                  objectFit: 'cover',
                  borderRadius: 2,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              />
            ) : null}
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createValues.isPublic}
                    onChange={(event) =>
                      setCreateValues((current) => ({
                        ...current,
                        isPublic: event.target.checked,
                      }))
                    }
                  />
                }
                label="Visible in app discovery"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createValues.isFeatured}
                    onChange={(event) =>
                      setCreateValues((current) => ({
                        ...current,
                        isFeatured: event.target.checked,
                      }))
                    }
                  />
                }
                label="Featured priority"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateCircle}
            disabled={creating || uploadingCoverPhoto}
          >
            {creating ? 'Creating…' : 'Create circle'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminPage>
  );
};

export default CirclesListPage;
